import FilterComponent from '@/components/filter/Filter/Filter';
import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { FilterType } from '@/models/filter/FilterType';
import containerStyles from '@/pages/PartnerScore/components/PartnerScoreContainer/PartnerScoreContainer.module.scss';
import TreeSelect from '@/storybook/components/FormCollection/TreeSelect/TreeSelect';
import ZippedPanel from '@/storybook/components/ZippedPanel/ZippedPanel';
import CrossIcon from '@/storybook/icons/CrossIcon';
import FilterIcon from '@/storybook/icons/FilterIcon';
import { TestIds } from '@pages/ChannelFocus/components/HcpCoverage/components/Filters/testIds';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './PartnerScoreFilter.module.scss';
import { testIds } from './testids';
import { PartnerScoreFiltersStorage, UsePartnerScoreFilterReturn } from './usePartnerScoreFilter';

const defaultValues: PartnerScoreFiltersStorage = {
  yearQuarter: [],
  surveyTitle: [],
};

interface PartnerScoreFilterProps {
  storedFilters: PartnerScoreFiltersStorage;
  yearQuarterOptions: UsePartnerScoreFilterReturn['yearQuarterOptions'];
  onFiltersChange: (filters: PartnerScoreFiltersStorage) => void;
}

function PartnerScoreFilter({ storedFilters, yearQuarterOptions, onFiltersChange }: PartnerScoreFilterProps) {
  const { t } = useTranslation();
  const { countries } = usePartnerScoreGlobalFilter();

  const methods = useForm<PartnerScoreFiltersStorage>({
    defaultValues: {
      ...defaultValues,
      ...storedFilters,
    },
  });

  const { control, reset } = methods;

  // Sync form when filters are updated externally (e.g. shared view applied).
  // Same pattern as Channel Focus AdditionalFilter: isExternalSync prevents
  // the write-back effect from re-storing the reset values.
  const isInitialMount = useRef(true);
  const prevStoredFiltersRef = useRef(storedFilters);
  const isExternalSync = useRef(false);

  useEffect(() => {
    if (!isEqual(prevStoredFiltersRef.current, storedFilters) && !isInitialMount.current) {
      prevStoredFiltersRef.current = storedFilters;
      isExternalSync.current = true;
      reset({
        ...defaultValues,
        ...storedFilters,
      });
    }
  }, [storedFilters, reset]);

  const watched = useWatch({ control });

  // Write form changes back via onFiltersChange (React state setter) — not
  // syncSessionStorage.  This avoids dispatching a custom event that would
  // re-trigger useSessionStorage listeners and cause prune/auto-select
  // effects to fight with the externally applied values.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isExternalSync.current) {
      isExternalSync.current = false;
      return;
    }
    const current: PartnerScoreFiltersStorage = {
      yearQuarter: (watched.yearQuarter as string[]) ?? [],
      surveyTitle: (watched.surveyTitle as string[]) ?? [],
    };
    // Skip if form values match storedFilters — avoids unnecessary writes
    // on mount and after external sync.
    if (isEqual(current, prevStoredFiltersRef.current)) return;
    onFiltersChange(current);
  }, [watched, onFiltersChange]);

  const zippedPanelConfig = useMemo(() => {
    return {
      zipperOpen: {
        'icon': <CrossIcon />,
        'label': t('main.channelFocus.hcpCoverage.filters.hideFilters'),
        'data-testid': TestIds.additionalFilter.hideFiltersButton,
      },
      zipperClosed: {
        icon: <FilterIcon />,
        label: t('main.channelFocus.hcpCoverage.filters.showFilters'),
      },
    };
  }, [t]);

  return (
    <ZippedPanel {...zippedPanelConfig} defaultState={true} className={containerStyles.zippedPanel}>
      <div className={containerStyles.container} data-testid={testIds.partnerScoreFilterContainer}>
        <FormProvider {...methods}>
          <div className={styles.filterItem}>
            <Controller
              name="yearQuarter"
              control={control}
              render={({ field }) => (
                <TreeSelect
                  label={t('main.hcpOverview.partnerScore.filter.yearQuarter')}
                  placeholder={t('main.hcpOverview.partnerScore.filter.yearQuarter')}
                  value={field.value}
                  options={yearQuarterOptions}
                  onChange={field.onChange}
                  disabled={!countries.length || !yearQuarterOptions.length}
                  data-testid={testIds.partnerScoreFilterYearQuarterDropdown}
                  hideCheckboxes
                  clearable={false}
                  nestedKeySort="chronological-desc"
                />
              )}
            />
          </div>
          <FilterComponent
            name="surveyTitle"
            control={control}
            filterKey={FilterType.PARTNER_SCORE_SURVEY_TITLE}
            classNames={styles.filterItem}
          />
        </FormProvider>
      </div>
    </ZippedPanel>
  );
}

export default PartnerScoreFilter;
